import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import styled from "styled-components";
import logo from '../../assets/images/xtreme.png';
import { FaLinkedin, FaTwitter, FaInstagram, FaFacebook } from 'react-icons/fa';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <FooterContainer>
      <GradientBar />
      <InnerContainer>
        <GridLayout>
          <BrandSection>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8 }}
            >
              <BrandContainer>
                <LogoImage
                  src={logo}
                  alt="Paisalocal"
                  whileHover={{ scale: 1.08 }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
                />
                <LogoSection>
                  <LogoText>
                    Xtreme Roofing
                  </LogoText>
                  <Tagline>
                    Professional <GradientText>& Reliable</GradientText>
                  </Tagline>
                </LogoSection>
              </BrandContainer>
              <Description>
                Your trusted partner for premium roofing and construction services. Over 20 years of excellence in the industry.
              </Description>
            </motion.div>
          </BrandSection>

          <NavSection>
            <NavColumn>
              <NavTitle>Company</NavTitle>
              <NavList>
                <NavItem>
                  <StyledLink to="/about">About Us</StyledLink>
                </NavItem>
                <NavItem>
                  <StyledLink to="/services">Our Services</StyledLink>
                </NavItem>
                <NavItem>
                  <StyledLink to="/contact">Contact Us</StyledLink>
                </NavItem>
              </NavList>
            </NavColumn>

            <NavColumn>
              <NavTitle>Resources</NavTitle>
              <NavList>
                <NavItem>
                  <StyledLink to="/calculator">Project Calculator</StyledLink>
                </NavItem>
                <NavItem>
                  <StyledLink to="/privacy-policy">Privacy Policy</StyledLink>
                </NavItem>
                <NavItem>
                  <StyledLink to="/terms">Terms of Service</StyledLink>
                </NavItem>
                <NavItem>
                  <StyledLink to="/faq">FAQ</StyledLink>
                </NavItem>
              </NavList>
            </NavColumn>
          </NavSection>
        </GridLayout>

        <Divider />

        <BottomSection>
          <BottomLeft>
            <Copyright>© {currentYear} Xtreme Roofing & Construction All rights reserved.</Copyright>
            <SocialLinks>
              <SocialIconLink href="https://linkedin.com" target="_blank" rel="noopener noreferrer">
                <FaLinkedin />
              </SocialIconLink>
              <SocialIconLink href="https://twitter.com" target="_blank" rel="noopener noreferrer">
                <FaTwitter />
              </SocialIconLink>
              <SocialIconLink href="https://instagram.com" target="_blank" rel="noopener noreferrer">
                <FaInstagram />
              </SocialIconLink>
              <SocialIconLink href="https://facebook.com/Xtremeroofingandconstructions" target="_blank" rel="noopener noreferrer">
                <FaFacebook />
              </SocialIconLink>
            </SocialLinks>
          </BottomLeft>
          <LocaleSelector>
            <GlobeIcon>🌐</GlobeIcon>
            <span>United States</span>
          </LocaleSelector>
        </BottomSection>

        <AdminLink>
          <Link to="/login">Customer Portal</Link>
        </AdminLink>
      </InnerContainer>
    </FooterContainer>
  );
};

const FooterContainer = styled.footer`
  background: var(--background);
  color: var(--text);
  padding: 4rem 0 2rem;
  position: relative;
  border-top: 1px solid rgba(255, 71, 71, 0.1);
`;

const GradientBar = styled.div`
  background: linear-gradient(90deg, #FF6B00 0%, #FF8A00 100%);
`;

const InnerContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 1rem;
`;

const GridLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(280px, 1fr) 2.5fr;
  gap: 8rem;
  margin-left: -1rem;

  @media (max-width: 1024px) {
    gap: 4rem;
    margin-left: 0;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
`;

const BrandSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding-right: 1rem;
`;

const BrandContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  margin-left: 0;
`;

const LogoImage = styled(motion.img)`
  width: 80px;
  height: 80px;
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  filter: drop-shadow(0 4px 8px rgba(255, 107, 0, 0.15));

  @media (max-width: 768px) {
    width: 60px;
    height: 60px;
  }
`;

const LogoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  padding-top: 0.15rem;
  margin-left: -0.5rem;
`;

const LogoText = styled.div`
  font-size: 1.5rem;
  font-weight: 800;
  background: linear-gradient(135deg, #FF6B00 0%, #FF8A00 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  letter-spacing: -0.02em;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

const Tagline = styled.div`
  font-size: 0.85rem;
  font-weight: 500;
  color: #CCCCCC;
  opacity: 0.9;
  letter-spacing: -0.01em;
  display: flex;
  align-items: center;
  gap: 0.35rem;
  white-space: nowrap;
  margin-top: -0.1rem;

  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

const Description = styled.p`
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.6);
  line-height: 1.6;
  margin: 1.25rem 0 0 0;
  max-width: 320px;
`;

const GradientText = styled.span`
  background: linear-gradient(135deg, #FF6B00 0%, #FF8A00 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: 500;
`;

const NavSection = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(180px, 1fr));
  gap: 6rem;
  padding-right: 2rem;
  margin-left: auto;
  max-width: 600px;
  justify-content: flex-end;

  @media (max-width: 1200px) {
    gap: 4rem;
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 3rem;
    padding-right: 0;
    margin-left: 0;
    max-width: none;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 2.5rem;
  }
`;

const NavColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.75rem;
  min-width: 160px;
`;

const NavTitle = styled.h3`
  font-size: 1rem;
  font-weight: 700;
  background: linear-gradient(135deg, #FF6B00 0%, #FF8A00 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  letter-spacing: 0.02em;
  margin: 0;
  padding-bottom: 0.25rem;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 24px;
    height: 2px;
    background: linear-gradient(90deg, #FF6B00, #FF8A00);
    border-radius: 2px;
  }
`;

const NavList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const NavItem = styled.li`
  font-size: 0.95rem;
  line-height: 1.4;
`;

const StyledLink = styled(Link)`
  color: rgba(255, 255, 255, 0.6);
  text-decoration: none;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  display: inline-block;
  padding: 0.25rem 0;

  &:after {
    content: '';
    position: absolute;
    width: 0;
    height: 2px;
    bottom: 0;
    left: 0;
    background: linear-gradient(90deg, #FF6B00, #FF8A00);
    transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    border-radius: 2px;
  }

  &:hover {
    color: #FF6B00;
    transform: translateX(4px);
    
    &:after {
      width: 100%;
    }
  }
`;

const Divider = styled.hr`
  border: none;
  height: 1px;
  background: rgba(255, 71, 71, 0.1);
  margin: 3rem 0 2rem;
`;

const BottomSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1.5rem;
`;

const BottomLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const SocialIconLink = styled.a`
  color: rgba(255, 255, 255, 0.6);
  font-size: 1.1rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: transparent;
  border: 1px solid rgba(255, 107, 0, 0.1);

  &:hover {
    color: var(--primary);
    background: rgba(255, 107, 0, 0.1);
    transform: translateY(-2px);
    border-color: rgba(255, 107, 0, 0.2);
  }
`;

const Copyright = styled.p`
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.6);
  margin: 0;
`;

const LocaleSelector = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  padding: 0.5rem 1rem;
  border-radius: 20px;
  background: transparent;

  &:hover {
    background: rgba(255, 107, 0, 0.1);
    color: #FF6B00;
  }
`;

const GlobeIcon = styled.span`
  font-size: 0.875rem;
`;

const AdminLink = styled.div`
  text-align: center;
  padding: 1rem;
  margin-top: 1rem;
  border-top: 1px solid rgba(255, 107, 0, 0.1);
  
  a {
    color: rgba(255, 255, 255, 0.6);
    font-size: 0.75rem;
    opacity: 0.7;
    text-decoration: none;
    transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    
    &:hover {
      opacity: 1;
      color: #FF6B00;
    }
  }
`;

export default Footer;
